import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { enqueueSnackbar } from '../../root/actions'
import {
    listWebhooks,
    getWebhook,
    getWebhookVersions,
    createWebhook,
    updateWebhook,
    deleteWebhook
} from '../api/webhooks-api'
import { INTEGRATIONS_AND_API_DATA_KEY } from './use-integrations-and-api-data'

export const WEBHOOKS_KEY = ['webhooks']
export const webhookKey = id => ['webhooks', id]

// Deliberately not ['webhooks', 'versions']: react-query matches keys by prefix, so that would
// make every webhook mutation refetch a list of constants.
export const WEBHOOK_VERSIONS_KEY = ['webhook-versions']

function useWebhookSnackbar() {
    const dispatch = useDispatch()
    const locale = useSelector(state => state.main.locale)

    const success = message =>
        dispatch(enqueueSnackbar({ message, options: { variant: 'success' } }))
    const error = message =>
        dispatch(enqueueSnackbar({ message, options: { variant: 'error' } }))

    return { locale, success, error }
}

export function useWebhooks() {
    const { locale, error } = useWebhookSnackbar()

    return useQuery(WEBHOOKS_KEY, listWebhooks, {
        refetchOnWindowFocus: false,
        onError: () => error(locale.integrations.webhooksTab.loadFailed)
    })
}

export function useWebhook(id, options = {}) {
    const { locale, error } = useWebhookSnackbar()

    return useQuery(webhookKey(id), () => getWebhook(id), {
        enabled: Boolean(id),
        refetchOnWindowFocus: false,
        onError: () => error(locale.integrations.webhooksTab.loadFailed),
        ...options
    })
}

export function useWebhookVersions() {
    return useQuery(WEBHOOK_VERSIONS_KEY, getWebhookVersions, {
        refetchOnWindowFocus: false,
        // A constant list, so it never goes stale within a session.
        staleTime: Infinity
    })
}

function useInvalidateWebhookCaches() {
    const queryClient = useQueryClient()
    return () => {
        queryClient.invalidateQueries(WEBHOOKS_KEY)
        queryClient.invalidateQueries(INTEGRATIONS_AND_API_DATA_KEY)
    }
}

export function useCreateWebhook() {
    const { locale, success, error } = useWebhookSnackbar()
    const invalidate = useInvalidateWebhookCaches()

    return useMutation(body => createWebhook(body), {
        onSuccess: () => {
            invalidate()
            success(locale.integrations.webhooksTab.webhookCreated)
        },
        onError: () => error(locale.integrations.webhooksTab.createFailed)
    })
}

export function useUpdateWebhook() {
    const { locale, success, error } = useWebhookSnackbar()
    const invalidate = useInvalidateWebhookCaches()

    return useMutation(({ id, body }) => updateWebhook(id, body), {
        onSuccess: () => {
            invalidate()
            success(locale.integrations.webhooksTab.webhookUpdated)
        },
        onError: () => error(locale.integrations.webhooksTab.updateFailed)
    })
}

export function useDeleteWebhook() {
    const { locale, success, error } = useWebhookSnackbar()
    const invalidate = useInvalidateWebhookCaches()

    return useMutation(id => deleteWebhook(id), {
        onSuccess: () => {
            invalidate()
            success(locale.integrations.webhooksTab.webhookDeleted)
        },
        onError: () => error(locale.integrations.webhooksTab.deleteFailed)
    })
}
